<template lang="pug">
Sheet(v-model:open="sheetOpen" @update:open="onSheetOpen")
  SheetTrigger(as-child)
    Button.ml-1(
      type="button"
      variant="ghost"
      size="icon"
      class="inline-flex h-5 w-5 align-middle"
      title="编辑后续月份协议配额"
      aria-label="编辑后续月份协议配额"
    )
      Icon(name="material-symbols:edit-outline-rounded" class="h-3.5 w-3.5")

  SheetContent(
    side="right"
    class="flex h-full w-[min(94vw,480px)] flex-col gap-0 overflow-hidden p-0 sm:max-w-[480px]"
  )
    SheetHeader(class="shrink-0 border-b px-5 py-4 pr-12 sm:px-7")
      SheetTitle 后续月份协议配额
      SheetDescription 仅在服务失效时清除。设置或清除仅影响后续月份，本周期额度不变。

    div(class="min-h-0 flex-1 space-y-6 overflow-y-auto px-5 py-5 sm:px-7")
      section.space-y-3
        Label(:for="`${compId}-standard`") 标准流量（GB，最高 9999）
        Input(:id="`${compId}-standard`" type="number" min="0.00000001" max="9999" step="any" v-model="monthlyQuoteInput" :disabled="saving" placeholder="标准流量（GB，最高 9999）")
        p.text-xs.text-muted-foreground(v-if="currentCustomMonthlyQuote !== null") 当前自定义标准流量：{{ currentCustomMonthlyQuote }}GB
        div.flex.items-center.gap-2
          Button(size="sm" :loading="saving" :disabled="saving" @click="setMonthlyQuote") 设置标准流量
          Button(size="sm" variant="outline" :disabled="saving || currentCustomMonthlyQuote === null" @click="clearMonthlyQuote") 清除

      section.space-y-3
        Label(:for="`${compId}-relay`") 中继流量（GB，最高 9999）
        Input(:id="`${compId}-relay`" type="number" min="0.00000001" max="9999" step="any" v-model="monthlyPreQuotaInput" :disabled="saving" placeholder="中继流量（GB，最高 9999）")
        p.text-xs.text-muted-foreground(v-if="currentCustomMonthlyPreQuota !== null") 当前自定义中继流量：{{ currentCustomMonthlyPreQuota }}GB
        div.flex.items-center.gap-2
          Button(size="sm" :loading="saving" :disabled="saving" @click="setMonthlyPreQuota") 设置中继流量
          Button(size="sm" variant="outline" :disabled="saving || currentCustomMonthlyPreQuota === null" @click="clearMonthlyPreQuota") 清除
</template>

<script lang="ts" setup>
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet'
import { toast } from '~/utils/sonner'

const service = useUserService()
const compId = useId()
const sheetOpen = ref(false)
const currentService = computed(() => service.currentService)
const monthlyQuoteInput = ref<string | number>('')
const monthlyPreQuotaInput = ref<string | number>('')
const saving = ref(false)
const currentCustomMonthlyQuote = computed(
  () => currentService.value?.extra.custom_monthly_quote ?? null,
)
const currentCustomMonthlyPreQuota = computed(
  () => currentService.value?.extra.custom_monthly_pre_quota ?? null,
)

watch(
  () => currentService.value?.id,
  () => syncMonthlyQuoteInput(currentService.value),
  { immediate: true },
)

function onSheetOpen(open: boolean) {
  if (open) {
    syncMonthlyQuoteInput(currentService.value)
  }
}

function parseMonthlyQuote(input: unknown): number | null {
  if (typeof input === 'string' && input.trim() === '') {
    return null
  }
  const value = Number(input)
  return Number.isFinite(value) && value > 0 && value <= 9999 ? value : null
}

function syncMonthlyQuoteInput(
  serviceValue: userServicesAttributes | null | undefined,
) {
  const quote = serviceValue?.extra.custom_monthly_quote
  const preQuota = serviceValue?.extra.custom_monthly_pre_quota
  monthlyQuoteInput.value = quote === undefined ? '' : String(quote)
  monthlyPreQuotaInput.value = preQuota === undefined ? '' : String(preQuota)
}

function applyCustomMonthlyQuotas(
  serviceValue: userServicesAttributes,
  customMonthlyQuote: number | null,
  customMonthlyPreQuota: number | null,
) {
  if (customMonthlyQuote === null) {
    delete serviceValue.extra.custom_monthly_quote
  } else {
    serviceValue.extra.custom_monthly_quote = customMonthlyQuote
  }

  if (customMonthlyPreQuota === null) {
    delete serviceValue.extra.custom_monthly_pre_quota
  } else {
    serviceValue.extra.custom_monthly_pre_quota = customMonthlyPreQuota
  }
}

async function saveMonthlyQuotas(
  customMonthlyQuote: number | null,
  customMonthlyPreQuota: number | null,
  changedQuota: 'quote' | 'pre_quota',
) {
  const targetService = currentService.value
  if (!targetService) {
    return
  }

  saving.value = true
  try {
    const data = await useAdmin.setCustomMonthlyQuote({
      serviceId: targetService.id,
      customMonthlyQuote,
      customMonthlyPreQuota,
    })
    if (data.result.service_id !== targetService.id) {
      throw new Error('更新月流量时服务 ID 不一致')
    }

    applyCustomMonthlyQuotas(
      targetService,
      data.result.custom_monthly_quote,
      data.result.custom_monthly_pre_quota,
    )
    if (currentService.value?.id === targetService.id) {
      syncMonthlyQuoteInput(targetService)
    }
    const isClearing = changedQuota === 'quote'
      ? customMonthlyQuote === null
      : customMonthlyPreQuota === null
    const quotaName = changedQuota === 'quote' ? '标准流量' : '中继流量'
    const savedValue = changedQuota === 'quote'
      ? data.result.custom_monthly_quote
      : data.result.custom_monthly_pre_quota
    toast({
      title: isClearing ? `自定义${quotaName}已清除` : `自定义${quotaName}已设置`,
      description: isClearing
        ? `下次续费将使用套餐默认${quotaName}，本周期额度不变。`
        : `下次续费将使用 ${savedValue}GB ${quotaName}，本周期额度不变。`,
    })
  } catch (error) {
    toast({
      title: '后续月份协议配额更新失败',
      description: error instanceof Error ? error.message : String(error),
      variant: 'destructive',
    })
  } finally {
    saving.value = false
  }
}

async function setMonthlyQuote() {
  const customMonthlyQuote = parseMonthlyQuote(monthlyQuoteInput.value)
  if (customMonthlyQuote === null) {
    toast({
      title: '月流量不正确',
      description: '请输入大于 0 且不超过 9999 的有效 GB 数值。',
      variant: 'destructive',
    })
    return
  }
  await saveMonthlyQuotas(
    customMonthlyQuote,
    currentCustomMonthlyPreQuota.value,
    'quote',
  )
}

async function clearMonthlyQuote() {
  await saveMonthlyQuotas(null, currentCustomMonthlyPreQuota.value, 'quote')
}

async function setMonthlyPreQuota() {
  const customMonthlyPreQuota = parseMonthlyQuote(monthlyPreQuotaInput.value)
  if (customMonthlyPreQuota === null) {
    toast({
      title: '中继月流量不正确',
      description: '请输入大于 0 且不超过 9999 的有效 GB 数值。',
      variant: 'destructive',
    })
    return
  }
  await saveMonthlyQuotas(
    currentCustomMonthlyQuote.value,
    customMonthlyPreQuota,
    'pre_quota',
  )
}

async function clearMonthlyPreQuota() {
  await saveMonthlyQuotas(currentCustomMonthlyQuote.value, null, 'pre_quota')
}
</script>
